import TypographyParagraph from '../../typography/TypographyParagraph'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { useRef, Suspense } from 'react'
import { Await } from '@remix-run/react'
import { asyncTruncateStringInMiddle, mapStatus } from '#app/utils/misc.tsx'

export default function ProofOfExhibitionTemplate({
	proofOfExhibition,
}: {
	proofOfExhibition: any
}) {
	const tooltipCopyExhibitionName = useRef<TooltipRefProps>(null)
	const tooltipCopyStartDate = useRef<TooltipRefProps>(null)
	const tooltipCopyEndDate = useRef<TooltipRefProps>(null)
	const tooltipCopyOwnerWallet = useRef<TooltipRefProps>(null)
	const tooltipCopyExhibitionWallet = useRef<TooltipRefProps>(null)
	const tooltipCopyExhibitionLocation = useRef<TooltipRefProps>(null)
	const tooltipCopyExternalLink = useRef<TooltipRefProps>(null)
	const tooltipCopyExhibitionDetails = useRef<TooltipRefProps>(null)
	const tooltipCopySignature = useRef<TooltipRefProps>(null)

	return (
		<>
			<div
				id="exhibitionName"
				className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(proofOfExhibition.properties.name)
					tooltipCopyExhibitionName.current?.open({
						anchorSelect: '#exhibitionName',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyExhibitionName.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyExhibitionName} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Exhibition:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{proofOfExhibition.properties.name}
				</TypographyParagraph>
			</div>
			<div className="flex flex-col sm:flex-row">
				<div
					id="startDate"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(
							proofOfExhibition.properties.startDate,
						)
						tooltipCopyStartDate.current?.open({
							anchorSelect: '#startDate',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyStartDate.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyStartDate} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Start date:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{proofOfExhibition.properties.startDate}
					</TypographyParagraph>
				</div>
				<div
					id="endDate"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(proofOfExhibition.properties.endDate)
						tooltipCopyEndDate.current?.open({
							anchorSelect: '#endDate',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyEndDate.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyEndDate} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						End date:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						{proofOfExhibition.properties.endDate}
					</TypographyParagraph>
				</div>
			</div>
			<div className="flex flex-col sm:flex-row">
				<div
					id="ownerWallet"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(
							proofOfExhibition.properties.ownerAddress,
						)
						tooltipCopyOwnerWallet.current?.open({
							anchorSelect: '#ownerWallet',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyOwnerWallet.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyOwnerWallet} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Owner Address:
					</TypographyParagraph>
					<Suspense
						fallback={
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all flex"
							>
								...{' '}
								{!proofOfExhibition.properties.artistInitiated &&
								mapStatus(proofOfExhibition) == 'Pending' ? (
									<div className="ml-2 flex w-fit items-center gap-x-1 rounded-full bg-[#EFB615] px-2.5 py-0">
										Pending
									</div>
								) : (
									''
								)}
							</TypographyParagraph>
						}
					>
						<Await
							resolve={(async () =>
								await asyncTruncateStringInMiddle(
									proofOfExhibition.properties.ownerAddress,
								))()}
						>
							{(ownerAddress: string) => (
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all flex"
								>
									{ownerAddress}{' '}
									{!proofOfExhibition.properties.artistInitiated &&
									mapStatus(proofOfExhibition) == 'Pending' ? (
										<div className="ml-2 flex w-fit items-center gap-x-1 rounded-full bg-[#EFB615] px-2.5 py-0">
											Pending
										</div>
									) : (
										''
									)}
								</TypographyParagraph>
							)}
						</Await>
					</Suspense>
				</div>
				<div
					id="exhibitionWallet"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(
							proofOfExhibition.properties.receiverAddress,
						)
						tooltipCopyExhibitionWallet.current?.open({
							anchorSelect: '#exhibitionWallet',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyExhibitionWallet.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyExhibitionWallet} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Exhibitor Address:
					</TypographyParagraph>
					<Suspense
						fallback={
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all flex"
							>
								...{' '}
								{proofOfExhibition.properties.artistInitiated &&
								mapStatus(proofOfExhibition) == 'Pending' ? (
									<div className="ml-2 flex w-fit items-center gap-x-1 rounded-full bg-[#EFB615] px-2.5 py-0">
										Pending
									</div>
								) : (
									''
								)}
							</TypographyParagraph>
						}
					>
						<Await
							resolve={(async () =>
								await asyncTruncateStringInMiddle(
									proofOfExhibition.properties.receiverAddress,
								))()}
						>
							{(receiverAddress: string) => (
								<TypographyParagraph
									size="medium"
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark break-all flex"
								>
									{receiverAddress}{' '}
									{proofOfExhibition.properties.artistInitiated &&
									mapStatus(proofOfExhibition) == 'Pending' ? (
										<div className="ml-2 flex w-fit items-center gap-x-1 rounded-full bg-[#EFB615] px-2.5 py-0">
											Pending
										</div>
									) : (
										''
									)}
								</TypographyParagraph>
							)}
						</Await>
					</Suspense>
				</div>
			</div>
			<div className="flex flex-col sm:flex-row">
				<div
					id="exhibitionLocation"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2 sm:border-r"
					onClick={() => {
						navigator.clipboard.writeText(proofOfExhibition.properties.location)
						tooltipCopyExhibitionLocation.current?.open({
							anchorSelect: '#exhibitionLocation',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyExhibitionLocation.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyExhibitionLocation} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						Exhibition Location:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark ml-1 truncate"
					>
						{proofOfExhibition.properties.location}
					</TypographyParagraph>
				</div>
				<div
					id="externalLink"
					className="flex flex-row justify-between border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark sm:w-1/2"
					onClick={() => {
						navigator.clipboard.writeText(proofOfExhibition.properties.url)
						tooltipCopyExternalLink.current?.open({
							anchorSelect: '#externalLink',
							content: 'Copied!',
						})
						setTimeout(() => {
							tooltipCopyExternalLink.current?.close()
						}, 2000)
					}}
				>
					<Tooltip imperativeModeOnly ref={tooltipCopyExternalLink} />
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark whitespace-nowrap mr-2"
					>
						External Link:
					</TypographyParagraph>
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark truncate"
					>
						{proofOfExhibition.properties.url}
					</TypographyParagraph>
				</div>
			</div>
			<div
				id="exhibitionDetails"
				className="flex flex-row border-b border-textInactiveLight p-4 hover:bg-uiSecondaryLight dark:border-textInactiveDark dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(
						proofOfExhibition.properties.description,
					)
					tooltipCopyExhibitionDetails.current?.open({
						anchorSelect: '#exhibitionDetails',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopyExhibitionDetails.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopyExhibitionDetails} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Comment:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{proofOfExhibition.properties.description}
				</TypographyParagraph>
			</div>
			<div
				id="signature"
				className="flex flex-row p-4 hover:bg-uiSecondaryLight dark:hover:bg-uiSecondaryDark"
				onClick={() => {
					navigator.clipboard.writeText(proofOfExhibition.signature)
					tooltipCopySignature.current?.open({
						anchorSelect: '#signature',
						content: 'Copied!',
					})
					setTimeout(() => {
						tooltipCopySignature.current?.close()
					}, 2000)
				}}
			>
				<Tooltip imperativeModeOnly ref={tooltipCopySignature} />
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Signature:
				</TypographyParagraph>
				<TypographyParagraph
					size="medium"
					additionalClasses="ml-2 text-textPrimaryLight dark:text-textPrimaryDark break-all"
				>
					{proofOfExhibition.signature}
				</TypographyParagraph>
			</div>
		</>
	)
}
